import { Schedule, User } from '@nextforce/api'
import { useCompanyPreferences } from 'company/queries/list'
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useStoredState } from 'shared/hooks/stored_state'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import { useFiltersContext } from './filters/context'
import { getAvailableSchedules, getDefaultSchedule } from './schedules'

export interface ScheduleContextValue {
  schedule?: Schedule
  availableSchedules: Schedule[]
  selectedUser?: User
  showCustomFilters?: boolean
  enableCustomEvents?: boolean
  setSchedule: React.Dispatch<React.SetStateAction<Schedule | undefined>>
  showSummaryTiles: boolean | undefined
  setShowSummaryTiles: React.Dispatch<React.SetStateAction<boolean>>
}

export const ScheduleContext = createContext({} as ScheduleContextValue)

interface Props {
  children: React.ReactNode
}

export function ScheduleProvider(props: Props) {
  const { data: user } = useUser()

  const { data } = useStores()
  const userStores =
    user?.role === 'helper'
      ? data?.stores.filter(
          store => store.preferences?.showStoreScheduleToHelpers
        )
      : data?.stores
  const companyId =
    data?.stores && data.stores.length > 0
      ? data.stores[0].company?.id
      : undefined
  const { data: companyPreferences } = useCompanyPreferences(companyId)
  const { activeStoreId } = useRecentStoresContext()
  const { setScheduleCalendar } = useFiltersContext()

  const showCustomFilters = companyPreferences?.showCustomFilters
  const enableCustomEvents = companyPreferences?.enableCustomEvents

  const availableSchedules = getAvailableSchedules(
    userStores,
    !!data?.hasShifts,
    showCustomFilters
  )

  const [schedule, setSchedule] = useState<Schedule | undefined>()

  const [showSummaryTiles, setShowSummaryTiles] = useShowSummaryTilesState()

  const onSetSchedule = useCallback(
    (newSchedule: Schedule) => {
      if (newSchedule.calendar === 'store') {
        if (!newSchedule.storeId && activeStoreId) {
          newSchedule.storeId = activeStoreId
        }
      }
      setSchedule(newSchedule)
    },
    [setSchedule, activeStoreId]
  )

  useEffect(() => {
    if (schedule?.calendar) {
      setScheduleCalendar(schedule?.calendar)
    }
  }, [schedule?.calendar, setScheduleCalendar])

  useEffect(() => {
    if (
      !schedule &&
      (user?.permissions?.canSeeHelperSchedule ||
        user?.permissions?.canSeeStoreSchedule)
    ) {
      setSchedule(
        getDefaultSchedule(
          user?.role ?? 'helper',
          availableSchedules,
          activeStoreId
        )
      )
    }
  }, [
    activeStoreId,
    availableSchedules,
    schedule,
    user?.permissions?.canSeeHelperSchedule,
    user?.permissions?.canSeeStoreSchedule,
    user?.role,
  ])

  return (
    <ScheduleContext.Provider
      value={{
        schedule: schedule,
        availableSchedules,
        showCustomFilters,
        enableCustomEvents,
        setSchedule: onSetSchedule,
        showSummaryTiles,
        setShowSummaryTiles,
      }}
    >
      {props.children}
    </ScheduleContext.Provider>
  )
}

export function useScheduleContext() {
  return useContext<ScheduleContextValue>(ScheduleContext)
}

/**
 * Custom hook for managing the showSummaryTiles state.
 * This hook ensures that the default value is false for new users.
 */
function useShowSummaryTilesState(): [
  boolean | undefined,
  Dispatch<SetStateAction<boolean | undefined>>
] {
  // Get the raw stored value
  const [storedValue, setStoredValue] =
    useStoredState<boolean>('show_summary_tiles')

  // For the returned value, convert null/undefined to false
  const effectiveValue =
    storedValue === null || storedValue === undefined ? false : storedValue

  return [effectiveValue, setStoredValue]
}
